"use client"

import Link from "next/link"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Home, ArrowLeft, Search, BookOpen } from "lucide-react"

export default function NotFound() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50 flex items-center justify-center p-4">
      <div className="max-w-2xl mx-auto text-center">
        <Card className="bg-white/80 backdrop-blur-sm border border-white/30 shadow-xl">
          <CardContent className="p-12">
            {/* Orange Lost Face Emoji */}
            <div className="mb-8">
              <div className="text-8xl mb-4 animate-bounce">
                😵
              </div>
              <div className="w-24 h-24 mx-auto bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center shadow-lg">
                <span className="text-4xl text-white font-bold">404</span>
              </div>
            </div>

            {/* Main Content */}
            <div className="space-y-6">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                Oops! Page Not Found
              </h1>
              
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                We couldn't find the page you're looking for. It might have been moved, deleted, or you entered the wrong URL.
              </p>

              {/* Interactive Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Button 
                  asChild 
                  className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-6 py-3 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                >
                  <Link href="/" className="flex items-center gap-2">
                    <Home className="w-5 h-5" />
                    Go Home
                  </Link>
                </Button>

                <Button 
                  variant="outline" 
                  asChild 
                  className="border-orange-300 text-orange-600 hover:bg-orange-50 px-6 py-3 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                >
                  <Link href="/blog" className="flex items-center gap-2">
                    <BookOpen className="w-5 h-5" />
                    Browse Blog
                  </Link>
                </Button>

                <Button 
                  variant="ghost" 
                  onClick={() => window.history.back()} 
                  className="text-gray-600 hover:text-gray-800 hover:bg-gray-100 px-6 py-3 rounded-lg transition-all duration-300 transform hover:scale-105"
                >
                  <ArrowLeft className="w-5 h-5 mr-2" />
                  Go Back
                </Button>
              </div>

              {/* Helpful Links */}
              <div className="mt-12 pt-8 border-t border-gray-200">
                <p className="text-sm text-gray-500 mb-4">
                  Looking for something specific? Try these popular pages:
                </p>
                <div className="flex flex-wrap justify-center gap-3">
                  <Link 
                    href="/pricing" 
                    className="text-blue-600 hover:text-blue-800 hover:underline text-sm font-medium transition-colors"
                  >
                    Pricing
                  </Link>
                  <span className="text-gray-300">•</span>
                  <Link 
                    href="/contact-us" 
                    className="text-blue-600 hover:text-blue-800 hover:underline text-sm font-medium transition-colors"
                  >
                    Contact Us
                  </Link>
                  <span className="text-gray-300">•</span>
                  <Link 
                    href="/blog" 
                    className="text-blue-600 hover:text-blue-800 hover:underline text-sm font-medium transition-colors"
                  >
                    Blog
                  </Link>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Fun Animation */}
        <div className="mt-8 text-center">
          <div className="inline-flex items-center gap-2 text-gray-400 text-sm">
            <Search className="w-4 h-4 animate-pulse" />
            <span>Still searching for what you need?</span>
          </div>
        </div>
      </div>
    </div>
  )
}